/**
 * LicenseBanner — specs/product-kernel/license-production-readiness.md.
 *
 * A small, persistent header element (not a tab) showing the current licence state. Reads
 * GET /v1/license-status on mount and on a slow poll (5 minutes — informational, not
 * realtime-critical). Never blocks the dashboard: a fetch failure is logged to the console and
 * rendered as the unlicensed state, same "never an availability incident" principle every other
 * part of this mechanism already follows.
 */

import React, { useEffect, useState } from 'react';
import { getLicenseStatus } from '../../api/riskClient';
import { useT } from '../../i18n';
import type { LicenseStatus } from '../../types/risk';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
/** Open question #2 in the spec — not yet confirmed with the founder, 14 days chosen as a
 * reasonable default. */
const EXPIRING_SOON_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

type Severity = 'neutral' | 'amber' | 'red';

const severityClasses: Record<Severity, string> = {
  neutral: 'text-wyrd-ink-dim',
  amber: 'text-amber-400',
  red: 'text-red-400',
};

/** specs/product-kernel/usage-dashboard-warnings.md — thresholds decided by the founder
 * (2026-10-25): amber at 80%, red at 100%. `null` when there's nothing to warn about (no
 * declared `monthly_volume_limit` at all, or usage genuinely below 80%) — this function never
 * invents a warning the license doesn't support. */
function describeUsage(
  status: LicenseStatus | null,
  t: (key: string, params?: Record<string, string | number>) => string
): { severity: 'amber' | 'red'; text: string } | null {
  if (!status || status.monthly_volume_limit === undefined) return null;
  const { current_month_completed_count: count, monthly_volume_limit: limit } = status;
  if (limit <= 0) return null;
  const fraction = count / limit;
  if (fraction >= 1) {
    return { severity: 'red', text: t('license.usageOverLimit', { count, limit }) };
  }
  if (fraction >= 0.8) {
    return { severity: 'amber', text: t('license.usageNearLimit', { count, limit }) };
  }
  return null;
}

function describe(
  status: LicenseStatus | null,
  error: boolean,
  t: (key: string, params?: Record<string, string | number>) => string,
  intlLocale: string
): { severity: Severity; text: string } {
  if (error || !status || !status.licensed) {
    return { severity: 'red', text: t('license.unlicensed') };
  }
  if (!status.expires_at) {
    // Signature-verified but somehow no expiry parsed — treat as invalid rather than guess.
    return { severity: 'red', text: t('license.invalid') };
  }
  const expiresAt = new Date(status.expires_at);
  const formattedDate = new Intl.DateTimeFormat(intlLocale, { dateStyle: 'medium' }).format(
    expiresAt
  );
  if (status.is_expired) {
    return { severity: 'red', text: t('license.expired', { date: formattedDate }) };
  }
  const msUntilExpiry = expiresAt.getTime() - Date.now();
  if (msUntilExpiry <= EXPIRING_SOON_WINDOW_MS) {
    return { severity: 'amber', text: t('license.expiringSoon', { date: formattedDate }) };
  }
  const tierSuffix = status.tier ? t('license.tierSuffix', { tier: status.tier }) : '';
  return {
    severity: 'neutral',
    text: t('license.active', { client: status.client_name ?? '' }) + tierSuffix,
  };
}

export const LicenseBanner: React.FC = () => {
  const { t, intlLocale } = useT();
  const [status, setStatus] = useState<LicenseStatus | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = () => {
      getLicenseStatus()
        .then((res) => {
          if (!cancelled) {
            setStatus(res);
            setError(false);
          }
        })
        .catch((e: Error) => {
          console.error('license-status fetch failed:', e.message);
          if (!cancelled) setError(true);
        });
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const { severity, text } = describe(status, error, t, intlLocale);
  const usageWarning = describeUsage(status, t);

  return (
    <span className="whitespace-nowrap">
      <span className={`text-xs ${severityClasses[severity]}`} title={text}>
        {text}
      </span>
      {status?.offline_mode && (
        <span
          className="text-xs text-amber-400 ml-2"
          title={t('license.offlineMode')}
        >
          {t('license.offlineMode')}
        </span>
      )}
      {usageWarning && (
        <span
          className={`text-xs ml-2 ${severityClasses[usageWarning.severity]}`}
          title={usageWarning.text}
        >
          {usageWarning.text}
        </span>
      )}
    </span>
  );
};

export default LicenseBanner;
